// Greater London postcode validation and normalization.
//
// Peopels.com currently operates in Greater London only, so we validate
// twice: first that the input is a syntactically valid UK postcode, then
// that the outward (first half) starts with a Greater London postcode area.
//
// Greater London areas: BR, CR, DA, E, EC, EN, HA, IG, KT, N, NW, RM, SE,
// SM, SW, TW, UB, W, WC, WD. Two-letter prefixes are listed BEFORE the
// single-letter ones so the alternation matches "EC"/"WC"/"NW"/"SE"/"SW"
// before falling through to "E"/"N"/"W".

const UK_POSTCODE_FULL =
  /^(GIR ?0AA|BFPO ?\d{1,4}|[A-PR-UWYZ]([0-9]{1,2}|([A-HK-Y][0-9]([0-9]|[ABEHMNPRV-Y])?)|[0-9][A-HJKPS-UW]) ?[0-9][ABD-HJLNP-UW-Z]{2})$/i;

const LONDON_OUTWARD =
  /^(EC|WC|NW|SE|SW|BR|CR|DA|EN|HA|IG|KT|RM|SM|TW|UB|WD|E|N|W)\d/i;

export const LONDON_POSTCODE_AREAS = [
  "BR", "CR", "DA", "E", "EC", "EN", "HA", "IG", "KT", "N",
  "NW", "RM", "SE", "SM", "SW", "TW", "UB", "W", "WC", "WD",
] as const;

export type PostcodeCheck =
  | { ok: true; value: string; pretty: string }
  | { ok: false; reason: "empty" | "too_short" | "format" | "out_of_area"; message: string };

export function normalizePostcode(raw: string): string {
  return raw.toUpperCase().replace(/\s+/g, "");
}

export function prettyPostcode(raw: string): string {
  const c = normalizePostcode(raw);
  if (c.length < 5) return c;
  return `${c.slice(0, c.length - 3)} ${c.slice(-3)}`;
}

/** Outward (e.g. "SW1A" from "SW1A 1AA"). Returns "" for malformed input. */
export function outwardPostcode(raw: string): string {
  const c = normalizePostcode(raw);
  if (c.length < 5) return "";
  return c.slice(0, c.length - 3);
}

export function isLondonPostcode(raw: string): boolean {
  return LONDON_OUTWARD.test(outwardPostcode(raw));
}

export function checkPostcode(raw: string): PostcodeCheck {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) {
    return { ok: false, reason: "empty", message: "Enter your postcode so we can show Helpers near you." };
  }
  const compact = normalizePostcode(trimmed);
  if (compact.length < 5) {
    return { ok: false, reason: "too_short", message: "Looks like a partial postcode — finish typing the inward part (e.g. 1AA)." };
  }
  if (!UK_POSTCODE_FULL.test(trimmed)) {
    return {
      ok: false,
      reason: "format",
      message: "That doesn't look like a UK postcode. Try the full code with a space — e.g. SW1A 1AA, E1 6AN or N1 9GU.",
    };
  }
  if (!isLondonPostcode(trimmed)) {
    return {
      ok: false,
      reason: "out_of_area",
      message: "We currently only cover Greater London. Try a London postcode — e.g. SW1A 1AA, E1 6AN or N1 9GU.",
    };
  }
  return { ok: true, value: compact, pretty: prettyPostcode(trimmed) };
}

export function isValidPostcode(raw: string): boolean {
  return checkPostcode(raw).ok;
}
